import { ApiError } from '@/api/client'
import { useResolveImport } from '@/api/hooks'
import type { ImportReport, ResolvedRecipe } from '@/api/types'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Feedback'
import { Textarea } from '@/components/ui/Field'
import { Note } from '@/components/ui/Note'
import { buttonClasses } from '@/components/ui/buttonStyles'
import { useToast } from '@/components/ui/toastContext'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { CheckCircle2, Copy, FileUp, RotateCcw, SearchCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  clearImportSession,
  extractJson,
  lineCounts,
  loadImportSession,
  saveImportSession,
  type ImportSession,
} from './session'
import { LlmContextCard } from './LlmContextCard'

/**
 * Import recipes written by an LLM (or exported from another instance).
 *
 * Nothing is written here: the file is resolved against the ingredient library,
 * and each recipe then opens as a pre-filled form to review and save.
 */
export function ImportPage() {
  useDocumentTitle('Import recipes')
  const toast = useToast()
  const resolve = useResolveImport()
  const [session, setSession] = useState<ImportSession | null>(loadImportSession)
  const [text, setText] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  // Coming back from a review: ingredients may have been created since the
  // report was made, so resolve the same file again. It is a read, never a write.
  const refreshed = useRef(false)
  useEffect(() => {
    if (refreshed.current || !session) return
    refreshed.current = true
    resolve.mutate(session.file, {
      onSuccess: (report) => {
        const next = { ...session, report }
        saveImportSession(next)
        setSession(next)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function check() {
    setParseError(null)
    resolve.reset()
    let file: unknown
    try {
      file = JSON.parse(extractJson(text))
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Not valid JSON.')
      return
    }
    try {
      const report = await resolve.mutateAsync(file)
      const next: ImportSession = { file, report, saved: {} }
      saveImportSession(next)
      setSession(next)
      setText('')
    } catch {
      // Shown below from resolve.error.
    }
  }

  async function readFile(file: File) {
    setText(await file.text())
    setParseError(null)
    resolve.reset()
  }

  function startOver() {
    clearImportSession()
    setSession(null)
    resolve.reset()
  }

  const validationErrors = formatValidationErrors(resolve.error)
  const otherError =
    resolve.error && validationErrors.length === 0
      ? resolve.error instanceof ApiError
        ? resolve.error.message
        : 'Could not check the file.'
      : null

  return (
    <PageShell>
      <PageHeader
        title="Import recipes"
        subtitle="Paste what your LLM wrote, review each recipe, save."
        actions={
          session ? (
            <Button variant="secondary" size="sm" onClick={startOver}>
              <RotateCcw size={14} aria-hidden />
              Start over
            </Button>
          ) : null
        }
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        <LlmContextCard />

        <div className="min-w-0 space-y-5">
          {session ? (
            <ReportCard session={session} refreshing={resolve.isPending} />
          ) : (
            <Card>
              <CardHeader
                title="2. Paste the result"
                subtitle="The JSON your LLM answered with. Surrounding text and code fences are fine."
              />
              <CardBody className="space-y-3">
                <Textarea
                  aria-label="Import JSON"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  rows={14}
                  spellCheck={false}
                  className="font-mono text-micro"
                  placeholder={'{\n  "format": "mealplanner",\n  "version": 1,\n  "recipes": [ … ]\n}'}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button variant="ghost" size="sm" onClick={() => fileInput.current?.click()}>
                    <FileUp size={14} aria-hidden />
                    Open file…
                  </Button>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="application/json,.json,.txt,.md"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      if (file) void readFile(file)
                      event.target.value = ''
                    }}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!text.trim()}
                    loading={resolve.isPending}
                    onClick={() => void check()}
                  >
                    <SearchCheck size={14} aria-hidden />
                    Check
                  </Button>
                </div>

                {parseError ? (
                  <Note tone="critical">That is not valid JSON: {parseError}</Note>
                ) : null}
                {otherError ? <Note tone="critical">{otherError}</Note> : null}
                {validationErrors.length > 0 ? (
                  <Note tone="critical">
                    <p className="font-medium">The file does not match the format.</p>
                    <ul className="mt-1.5 space-y-0.5 font-mono text-micro">
                      {validationErrors.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 -ml-2"
                      onClick={() =>
                        void copy(
                          `The importer rejected the file:\n${validationErrors.join('\n')}`,
                          'Errors copied. Paste them back to your LLM.',
                          toast,
                        )
                      }
                    >
                      <Copy size={14} aria-hidden />
                      Copy errors for the LLM
                    </Button>
                  </Note>
                ) : null}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </PageShell>
  )
}

function ReportCard({ session, refreshing }: { session: ImportSession; refreshing: boolean }) {
  const { report, saved } = session
  const open = report.recipes.filter((_, index) => saved[index] === undefined).length

  return (
    <Card>
      <CardHeader
        title="2. Review and save"
        subtitle={
          report.recipes.length === 0
            ? 'The file has no recipes.'
            : open === 0
              ? 'Every recipe in this import is saved.'
              : `${open} of ${report.recipes.length} ${report.recipes.length === 1 ? 'recipe' : 'recipes'} still to review.`
        }
        actions={refreshing ? <Spinner label="Updating" /> : null}
      />
      <CardBody className="space-y-3">
        <IngredientNotes report={report} />
        <ul className="divide-y divide-line">
          {report.recipes.map((recipe, index) => (
            <RecipeRow
              key={`${index}-${recipe.recipe.name}`}
              recipe={recipe}
              index={index}
              savedId={saved[index]}
            />
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

function IngredientNotes({ report }: { report: ImportReport }) {
  const differs = report.ingredient_notes.filter((note) => note.kind === 'differs')
  const unused = report.ingredient_notes.filter((note) => note.kind === 'unused')
  if (differs.length === 0 && unused.length === 0) return null
  return (
    <Note tone="info">
      {differs.length > 0 ? (
        <p>
          Already in your library with other macros, so your values are kept:{' '}
          {differs.map((note) => note.name).join(', ')}.
        </p>
      ) : null}
      {unused.length > 0 ? (
        <p>Defined in the file but not used by any recipe: {unused.map((note) => note.name).join(', ')}.</p>
      ) : null}
    </Note>
  )
}

function RecipeRow({
  recipe,
  index,
  savedId,
}: {
  recipe: ResolvedRecipe
  index: number
  savedId: number | undefined
}) {
  const counts = lineCounts(recipe)
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink">{recipe.recipe.name}</p>
        <p className="tabular mt-0.5 text-micro text-ink-faint">
          {counts.ready} ready
          {counts.create > 0 ? ` · ${counts.create} to create` : ''}
          {counts.attention > 0 ? (
            <span className="text-caution"> · {counts.attention} need attention</span>
          ) : null}
        </p>
        {savedId === undefined && recipe.name_conflict !== null ? (
          <p className="mt-0.5 text-micro text-caution">
            A recipe with this name exists.{' '}
            <Link to={`/recipes/${recipe.name_conflict}`} className="underline">
              Open it
            </Link>
          </p>
        ) : null}
      </div>
      {savedId !== undefined ? (
        <Link
          to={`/recipes/${savedId}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-positive"
        >
          <CheckCircle2 size={15} aria-hidden />
          Saved
        </Link>
      ) : (
        <Link to={`/recipes/new?import=${index}`} className={buttonClasses('primary', 'sm')}>
          Review
        </Link>
      )}
    </li>
  )
}

async function copy(
  text: string,
  message: string,
  toast: { success: (m: string) => void; error: (m: string) => void },
) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(message)
  } catch {
    toast.error('Could not copy to the clipboard.')
  }
}

/** `recipes.0.ingredients.2.grams: Extra inputs are not permitted`, one per error. */
function formatValidationErrors(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.code !== 'validation_error') return []
  const errors = error.context.errors
  if (!Array.isArray(errors)) return []
  return errors.map((item: { loc?: unknown; msg?: unknown }) => {
    const loc = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body') : []
    const path = loc.join('.')
    return `${path || '(file)'}: ${String(item.msg ?? 'invalid')}`
  })
}
